import { useEffect, useState, useCallback } from 'react'
import ErrorDisplay from '../../components/error/ErrorDisplay'
import { Grid, Box, Stack, TextField, Button } from '@mui/material'
import { DeleteSessionParams } from './DeleteSession'
import { getHeaders } from '../../utils/common'
import { DialogWrapper } from '../../components/general/DialogWrapper'
import Loading from '../../Loading'

/**
 * Component for updating a session.
 *
 * @param {Object} params - The parameters for rendering the session update form.
 * @param {Object} params.session - The session data to be updated.
 * @param {function} params.onDelete - A callback function to execute after successful session update.
 * @param {boolean} params.isOpen - Indicates whether the session update form is open.
 * @param {function} params.closeModal - A callback function to close the session update form.
 * @returns {JSX.Element} The JSX element representing the `UpdateSession` component.
 */
export default function UpdateSession({ session, onDelete, isOpen, closeModal }: DeleteSessionParams) {
  const [name, setName] = useState('')
  const [createError, setCreateError] = useState<Error | null>(null)
  const [isCreateInProgress, setIsCreateInProgress] = useState(false)
  const myHeaders = getHeaders()

  useEffect(() => {
    setIsCreateInProgress(false)
    setName(session?.name || '')
    setCreateError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const onSubmit = useCallback(
    (ctx:any) => {
      ctx.preventDefault()

      if (name === '') {
        setCreateError(new Error('An session name must be specified'))
        return
      }

      setIsCreateInProgress(true)
      myHeaders.append('Content-Type', 'application/json')
      fetch(`/mqttapi/v1/session`, {
        method: 'PUT',
        headers: myHeaders,
        body: JSON.stringify({
          id: session?.id,
          name: name,
        }),
      })
        .then(async (response) => {
          if (response.ok) {
            return response.json()
          }
          const text = await response.text()
          throw new Error(`${response.statusText}: ${text}`)
        })
        .then((json) => {
          onDelete()
          closeModal()
        })
        .catch((error) => setCreateError(error))
        .finally(() => setIsCreateInProgress(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, setIsCreateInProgress, onDelete]
  )

  return (
    <DialogWrapper isOpen={isOpen} closeModal={closeModal} maxWidth="sm">
      <h4>Update Session</h4>
      <Grid container spacing={2} sx={{ marginBottom: '5px', m: 1 }}>
        <Grid item sm={12} md={12}>
          <Stack direction="row" spacing={2}>
            <Box
              component="form"
              sx={{
                '& > :not(style)': { m: 1 },
              }}
              noValidate
              autoComplete="off"
            >
              <TextField
                value={name}
                label="Session Name"
                color="primary"
                onChange={(event) => setName(event.currentTarget.value)}
                placeholder="carbon_session"
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>
      {isCreateInProgress ? (
        <Loading action="Creating..." />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'right', gap: '20px' }}>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            Update
          </Button>
          <Button variant="contained" color="primary" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
      )}
      {createError !== null ? <ErrorDisplay err={createError} /> : null}
    </DialogWrapper>
  )
}
